import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { TABLE_PAGINATION_PIN_EVENT, TABLE_PAGINATION_PIN_HEIGHT_VAR } from '../../CustomScrollbar/constants';
import { isElementBottomVisible, isElementVisible } from '../../CustomScrollbar/utils';
function clearPinStyles(pagination, table) {
    if (pagination) {
        pagination.style.position = '';
        pagination.style.bottom = '';
        pagination.style.left = '';
        pagination.style.width = '';
    }
    if (table) {
        table.style.setProperty(TABLE_PAGINATION_PIN_HEIGHT_VAR, '0px');
    }
}
/**
 * Pins listing pagination to the viewport bottom when the table overflow
 * region extends below the fold. `position: sticky; bottom: 0` cannot pull
 * the pager into view from below, so this matches CustomScrollbar's fixed
 * thumb: pin while the table bottom is off-screen, unpin when it is visible.
 *
 * Sets `--qmb-table-pagination-pin-height` on `.qmb-ui-table` so the
 * horizontal thumb sits above the pager instead of overlapping it.
 */
export function usePinnedTablePagination(enabled, hasPagination = true) {
    const tableRef = useRef(null);
    const paginationRef = useRef(null);
    const [isPinned, setIsPinned] = useState(false);
    const [pinHeight, setPinHeight] = useState(0);
    const isPinnedRef = useRef(false);
    const pinHeightRef = useRef(0);
    const update = useCallback(() => {
        const table = tableRef.current;
        const pagination = paginationRef.current;
        if (!enabled || !hasPagination || !table || !pagination) {
            if (isPinnedRef.current) {
                isPinnedRef.current = false;
                setIsPinned(false);
            }
            if (pinHeightRef.current !== 0) {
                pinHeightRef.current = 0;
                setPinHeight(0);
            }
            clearPinStyles(pagination, table);
            table === null || table === void 0 ? void 0 : table.dispatchEvent(new Event(TABLE_PAGINATION_PIN_EVENT, { bubbles: true }));
            return;
        }
        const scrollRegion = table.querySelector('.qmb-ui-table__scroll');
        const pinTarget = scrollRegion !== null && scrollRegion !== void 0 ? scrollRegion : table;
        const shouldPin = isElementVisible(pinTarget) && !isElementBottomVisible(pinTarget);
        const measuredHeight = pagination.getBoundingClientRect().height;
        const nextHeight = shouldPin ? measuredHeight : 0;
        const tableRect = table.getBoundingClientRect();
        table.style.setProperty(TABLE_PAGINATION_PIN_HEIGHT_VAR, `${nextHeight}px`);
        if (shouldPin) {
            pagination.style.position = 'fixed';
            pagination.style.bottom = '0';
            pagination.style.left = `${tableRect.left}px`;
            pagination.style.width = `${tableRect.width}px`;
        }
        else {
            clearPinStyles(pagination, table);
            table.style.setProperty(TABLE_PAGINATION_PIN_HEIGHT_VAR, '0px');
        }
        if (isPinnedRef.current !== shouldPin) {
            isPinnedRef.current = shouldPin;
            setIsPinned(shouldPin);
        }
        if (pinHeightRef.current !== nextHeight) {
            pinHeightRef.current = nextHeight;
            setPinHeight(nextHeight);
        }
        table.dispatchEvent(new Event(TABLE_PAGINATION_PIN_EVENT, { bubbles: true }));
    }, [enabled, hasPagination]);
    useLayoutEffect(() => {
        update();
        if (!enabled || !hasPagination) {
            return undefined;
        }
        const scrollOptions = { capture: true, passive: true };
        window.addEventListener('scroll', update, scrollOptions);
        window.addEventListener('resize', update);
        const table = tableRef.current;
        const scrollRegion = table === null || table === void 0 ? void 0 : table.querySelector('.qmb-ui-table__scroll');
        scrollRegion === null || scrollRegion === void 0 ? void 0 : scrollRegion.addEventListener('scroll', update, scrollOptions);
        const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
        if (resizeObserver && paginationRef.current) {
            resizeObserver.observe(paginationRef.current);
        }
        if (resizeObserver && table) {
            resizeObserver.observe(table);
        }
        return () => {
            window.removeEventListener('scroll', update, scrollOptions);
            window.removeEventListener('resize', update);
            scrollRegion === null || scrollRegion === void 0 ? void 0 : scrollRegion.removeEventListener('scroll', update, scrollOptions);
            resizeObserver === null || resizeObserver === void 0 ? void 0 : resizeObserver.disconnect();
        };
    }, [enabled, hasPagination, update]);
    return { tableRef, paginationRef, isPinned, pinHeight };
}
